import { Button } from "@/components/ui/button";
import { Shield, Menu } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useState } from "react";

const Header = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleFeaturesClick = () => {
    if (location.pathname !== '/') {
      // If not on homepage, navigate there first
      navigate('/');
      // Wait for navigation to complete, then scroll to section
      setTimeout(() => {
        const element = document.getElementById('why-companies-choose-solidwraps');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If already on homepage, just scroll to section
      const element = document.getElementById('why-companies-choose-solidwraps');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false);
  };

  return (
    <header className="fixed top-0 w-full max-w-full bg-background/95 backdrop-blur-sm border-b border-border z-50 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 max-w-full overflow-hidden">
        <div className="flex items-center justify-between min-w-0">
          <Link to="/" className="flex items-center space-x-1 sm:space-x-2 hover:opacity-80 transition-opacity min-w-0 flex-shrink">
            <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-primary flex-shrink-0" />
            <span className="text-lg sm:text-2xl font-bold text-foreground truncate">SolidWraps</span>
          </Link>
          
          <nav className="hidden md:flex items-center space-x-8 flex-shrink-0">
            <button 
              onClick={handleFeaturesClick}
              className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap bg-transparent border-none cursor-pointer"
            >
              Features
            </button>
            <Link to="/how-to-use" className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
              Use Cases
            </Link>
            <Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
              Pricing
            </Link>
            <Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">
              Contact
            </Link>
          </nav>

          <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
            <Button variant="ghost" className="hidden sm:flex text-muted-foreground hover:text-foreground" asChild>
              <a href="/auth">Sign In</a>
            </Button>
            <Button variant="cta" size="sm" className="text-xs sm:text-sm px-2 sm:px-4 whitespace-nowrap" asChild>
              <a href={user ? "/admin" : "/auth?tab=signup"}>
                {user ? "Dashboard" : "Sign up"}
              </a>
            </Button>
            
            {/* Mobile Navigation */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="md:hidden p-2">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <Shield className="h-6 w-6 text-primary" />
                    SolidWraps
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-4 mt-8">
                  <button 
                    onClick={handleFeaturesClick}
                    className="text-lg text-muted-foreground hover:text-foreground transition-colors py-2 border-b border-border bg-transparent border-none cursor-pointer text-left w-full"
                  >
                    Features
                  </button>
                  <Link 
                    to="/how-to-use" 
                    className="text-lg text-muted-foreground hover:text-foreground transition-colors py-2 border-b border-border"
                    onClick={() => setIsOpen(false)}
                  >
                    Use Cases
                  </Link>
                  <Link 
                    to="/pricing" 
                    className="text-lg text-muted-foreground hover:text-foreground transition-colors py-2 border-b border-border"
                    onClick={() => setIsOpen(false)}
                  >
                    Pricing
                  </Link>
                  <Link 
                    to="/contact" 
                    className="text-lg text-muted-foreground hover:text-foreground transition-colors py-2 border-b border-border"
                    onClick={() => setIsOpen(false)}
                  >
                    Contact
                  </Link>
                  <div className="pt-4 space-y-3">
                    <Button variant="ghost" className="w-full justify-start text-lg" asChild>
                      <a href="/auth" onClick={() => setIsOpen(false)}>Sign In</a>
                    </Button>
                    <Button variant="cta" className="w-full text-lg" asChild>
                      <a href={user ? "/admin" : "/auth?tab=signup"} onClick={() => setIsOpen(false)}>
                        {user ? "Dashboard" : "Sign Up"}
                      </a>
                    </Button>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;